import React from 'react';
import { Modal, Tag, Alert, Spin, Empty, Button, Space, Typography } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import AiAgreementIndicator from './AiAgreementIndicator';

const { Text, Paragraph } = Typography;

/**
 * AI-drafted advisory-firm profile — review-before-use surface (Commandment IV).
 *
 * A.5.15-adjacent note is not relevant here (this is LA firm profile, not a client control),
 * but the same guardrail discipline applies: nothing from this draft is ever written into the
 * firm's saved profile except through an explicit, per-field "Apply" click the LA makes here.
 * Mirrors the established convention in ProjectOnboarding.jsx's `aiNamingModal`
 * (Tag color="purple" "AI drafted — review before use"). The run-agreement score is rendered by
 * AiAgreementIndicator (neutral, disclaimed, never a fabricated figure when absent).
 *
 * `draft` fields are read defensively (this wave the backend contract for
 * POST /api/v1/me/advisory-firm/ai-profile is documented but not live — see
 * .claude/design/la-onboarding-razorpay.md Workstream A). `paymentLinksJson` is deliberately
 * NEVER read or rendered here, per the spec's 2026-09-28 amendment: AI enrichment must never
 * touch a Lead Advisor's payment-collection links.
 */
const FIELD_LABELS = {
    firmName: 'Firm name',
    legalName: 'Legal name',
    city: 'City',
    state: 'State',
    country: 'Country',
    aboutFirm: 'About the firm',
    founderSummary: 'Founder / leadership profile',
};

const AdvisoryFirmAiDraftModal = ({ open, loading, error, draft, appliedKeys, onApply, onApplyAll, onClose }) => {
    const fieldKeys = draft ? Object.keys(FIELD_LABELS).filter((k) => draft[k] != null && draft[k] !== '') : [];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={600}
            title="AI-drafted firm profile - review before use"
            footer={
                <Space>
                    <Button onClick={onClose}>Close</Button>
                    {fieldKeys.length > 0 && (
                        <Button type="primary" icon={<CheckOutlined />} onClick={onApplyAll}>
                            Apply all suggestions
                        </Button>
                    )}
                </Space>
            }
            destroyOnClose
        >
            <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <Tag color="purple">AI drafted - review before use</Tag>

                {loading && (
                    <div style={{ textAlign: 'center', padding: '32px 0' }}>
                        <Spin size="large" />
                        <Paragraph style={{ marginTop: 12, color: 'var(--color-text-muted)' }}>
                            Searching the firm website and LinkedIn…
                        </Paragraph>
                    </div>
                )}

                {!loading && error && (
                    <Alert
                        type="error"
                        showIcon
                        message="AI enrichment unavailable"
                        description={error}
                    />
                )}

                {!loading && !error && draft && (
                    <>
                        <AiAgreementIndicator
                            confidence={draft.confidence}
                            runs={draft.confidenceRuns}
                            validSamples={draft.validSamples}
                            reasoningRefs={draft.reasoningRefs}
                            unavailableReason={draft.confidenceUnavailableReason}
                        />

                        {fieldKeys.length === 0 ? (
                            <Empty description="No suggestions returned." />
                        ) : (
                            fieldKeys.map((key) => (
                                <div
                                    key={key}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'flex-start',
                                        gap: 12,
                                        padding: '10px 0',
                                        borderBottom: '1px solid var(--color-border)',
                                    }}
                                >
                                    <div style={{ minWidth: 0 }}>
                                        <Text style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                            {FIELD_LABELS[key]}
                                        </Text>
                                        <Paragraph style={{ margin: '2px 0 0', fontSize: 13 }}>{String(draft[key])}</Paragraph>
                                    </div>
                                    <Button
                                        size="small"
                                        type={appliedKeys?.has(key) ? 'default' : 'primary'}
                                        disabled={appliedKeys?.has(key)}
                                        onClick={() => onApply(key, draft[key])}
                                    >
                                        {appliedKeys?.has(key) ? 'Applied' : 'Apply'}
                                    </Button>
                                </div>
                            ))
                        )}

                        <Alert
                            type="info"
                            showIcon
                            message="Payment links are never suggested by AI. Add UPI / Razorpay Payment Page links yourself in the next step."
                        />
                    </>
                )}
            </Space>
        </Modal>
    );
};

export default AdvisoryFirmAiDraftModal;
